/**
 * BoardQAPanel — generates the Anticipated Board Q&A / objection-handling sheet
 * (Claude, background job) and downloads it as an editable DOCX. Mirrors the
 * ProcessModelPanel fire-and-poll pattern. Framework-agnostic.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquareWarning, RefreshCw, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../../lib/api";
import GenerationProgress from "./GenerationProgress";

async function downloadBoardQaDocx(runId) {
  try {
    const res = await fetch(`${API_BASE}/admin/assessments/runs/${runId}/export/boardqa`, { credentials: "include" });
    if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = m ? m[1] : "board-qa.docx";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Board Q&A briefing (DOCX) downloaded");
  } catch (err) {
    toast.error(`Download failed: ${err.message || err}`);
  }
}

export default function BoardQAPanel({ runId }) {
  const [bq, setBq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastError, setLastError] = useState(null);
  const pollRef = useRef(null);
  const toastRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/board-qa`);
      setBq(data.board_qa);
      setLastError(data.last_error || null);
      if (data.status === "generating") setGenerating(true);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error(e?.response?.data?.detail || "Load failed");
      setBq(null);
    } finally { setLoading(false); }
  }, [runId]);

  useEffect(() => { load(); return () => stopPolling(); }, [load, stopPolling]);

  useEffect(() => {
    if (!generating || pollRef.current) return;
    const startedAt = Date.now();
    const MAX_POLL_MS = 9 * 60 * 1000;
    const tick = async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        stopPolling(); setGenerating(false);
        toast.error("Taking longer than expected. Refresh or regenerate.", { id: toastRef.current });
        toastRef.current = null; return;
      }
      try {
        const { data } = await api.get(`/admin/assessments/runs/${runId}/board-qa`);
        setBq(data.board_qa);
        setLastError(data.last_error || null);
        const st = data.status || data.board_qa?.status || "ready";
        if (st === "ready") {
          stopPolling(); setGenerating(false);
          if (data.last_error) toast.error(data.last_error, { id: toastRef.current, duration: 8000 });
          else toast.success("Board Q&A ready", { id: toastRef.current });
          toastRef.current = null;
        } else if (st === "failed") {
          stopPolling(); setGenerating(false);
          toast.error(data.last_error || data.board_qa?.error || "Generation failed", { id: toastRef.current, duration: 8000 });
          toastRef.current = null;
        }
      } catch { /* transient */ }
    };
    pollRef.current = setInterval(tick, 4000);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, runId]);

  const generate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    const ts = toast.loading("Anticipating the toughest board questions (~1–2 min). You can switch tabs.");
    toastRef.current = ts;
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/board-qa`, {
        force_regenerate: !!bq && bq.status === "ready",
      });
      setBq(data.board_qa);
      if (data.cached) { setGenerating(false); toast.success("Loaded from cache", { id: ts }); }
      else if (data.status === "ready") { setGenerating(false); toast.success("Ready", { id: ts }); }
      else { toast.loading("Generating in the background…", { id: ts }); }
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || e.message || "Generation failed", { id: ts });
    }
  }, [runId, bq, generating]);

  const pl = bq?.payload;
  const ready = bq?.status === "ready" || (bq && !bq.status);

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="board-qa-panel">
      <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
        <MessageSquareWarning size={14}/> Anticipated board Q&amp;A
      </div>
      <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
        The toughest questions a sceptical board is likely to ask — across Value, Risk, Delivery, Cost and Governance —
        each with a crisp, <strong>evidence-anchored answer</strong> the executive can deliver verbatim. Downloads as an editable DOCX.
      </p>

      {bq?.status === "failed" && bq?.error && (
        <div className="text-[11px] text-red-700 mt-2">Generation failed — {bq.error}</div>
      )}

      {lastError && (
        <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-2 rounded" data-testid="board-qa-last-error">
          Last regeneration didn't complete — {lastError} Your previous version is still shown below and used in exports.
        </div>
      )}

      <GenerationProgress active={generating} label="Anticipating questions" typical="~1–2 min" testid="board-qa-generation-progress" />

      <button
        type="button"
        onClick={generate}
        disabled={generating}
        className="w-full mt-3 bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-60 text-white text-[13px] font-medium px-3 py-2 flex items-center justify-center gap-2 transition-colors"
        data-testid="board-qa-generate"
      >
        {generating ? <RefreshCw size={14} className="animate-spin"/> : <MessageSquareWarning size={14}/>}
        {generating ? "Generating…" : bq && ready ? "Regenerate Q&A" : "Generate board Q&A"}
      </button>

      {ready && pl && (
        <div className="mt-4 space-y-3">
          <div className="border-l-2 border-[#7737F6] pl-3">
            <p className="text-[12px] text-zinc-600">{pl.summary}</p>
            <div className="text-[11px] text-zinc-400 mt-1">
              {pl.questions?.length || 0} anticipated questions
              {bq?.payload?._skeleton ? " · deterministic draft" : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={() => downloadBoardQaDocx(runId)}
            className="w-full text-left flex items-center justify-between px-3 py-2.5 border-2 border-[#7737F6] bg-[#7737F6] text-white text-[13px] font-medium hover:bg-[#5f29c9] transition-colors"
            data-testid="board-qa-download-docx"
          >
            <span>Download board Q&amp;A briefing (DOCX) — editable</span>
            <FileDown size={14} className="text-white"/>
          </button>
        </div>
      )}
    </div>
  );
}
